import {
  CapReachedError,
  RegressionNotAllowedError,
  ReopenConflictError,
  ReopenNotAllowedError,
  SnoozeCapError,
  type AccountPattern,
  type ActivationContext,
  type EvidenceInfo,
  type HistoryActorType,
  type MatchedContact,
  type Outcome,
  type PassReason,
  type RecipientBrief,
  type RecipientHistoryEntry,
  type RecipientSignal,
  type RecipientTokenAdapter,
  type SnoozeResult,
  type StageId,
  type WhyForYou,
} from "../types";
import type { SignalRelationshipResult } from "@/components/RelationshipResults";

/**
 * Live token adapter (W2) — bound to the deployed v08 magic-link functions.
 *
 * The recipient is session-less: every call is a raw POST carrying only the
 * publishable apikey (NO Authorization header) — the opaque token in the body is
 * the sole credential, resolved server-side to the internal recipient. This is
 * deliberate so a lingering admin session in the same browser never leaks into a
 * recipient view. No client-side Supabase writes; all mutations are Edge
 * Function ledger events attributed to actor_type=token_recipient.
 *
 * Known load-time gaps (resolve/brief do not expose these; bound where the
 * server provides them, defaulted otherwise — reported to Codex):
 *   - expiry countdown + snooze budget (only create/snooze return expires_at)
 *   - assigner display name (session-less recipient cannot read user_profiles)
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const PUBLISHABLE = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const DAY = 86_400_000;
/** Initial link validity; used only for the load-time countdown (see gaps). */
const DEFAULT_VALIDITY_DAYS = 14;
const DEFAULT_SNOOZE_MAX = 2;

function passReasonLabel(code: PassReason["code"]): string {
  if (code === "not_a_fit") return "Not a fit";
  if (code === "at_capacity") return "At capacity";
  if (code === "pto") return "Going on PTO";
  return "Other";
}

export interface Invoked {
  status: number;
  data: Record<string, unknown> | undefined;
  ok: boolean;
  raw: string;
}

/** Session-less token invoke: apikey only, no Authorization. */
async function tokenInvoke(
  name: string,
  body: Record<string, unknown>,
): Promise<Invoked> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: "POST",
    headers: { apikey: PUBLISHABLE, "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const raw = await res.text();
  let json: { data?: Record<string, unknown> } = {};
  try {
    json = JSON.parse(raw) as { data?: Record<string, unknown> };
  } catch {
    /* non-JSON body */
  }
  return { status: res.status, data: json?.data, ok: res.ok, raw };
}

/** A dead token: bad status, or a valid:false envelope from resolve/brief. */
function isDead(r: Invoked): boolean {
  return r.status === 401 || r.status === 403 || r.status === 404 || r.data?.["valid"] === false;
}

export function requireTokenRead(name: string, result: Invoked): Record<string, unknown> | null {
  if (isDead(result)) return null;
  if (!result.ok || !result.data) {
    throw new Error(`${name}: ${result.status}: ${result.raw || "Missing response data"}`);
  }
  return result.data;
}

const g = (o: unknown, k: string): unknown =>
  o && typeof o === "object" ? (o as Record<string, unknown>)[k] : undefined;
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strOrNull = (v: unknown): string | null => (typeof v === "string" ? v : null);
const num = (v: unknown): number => (typeof v === "number" ? v : 0);

function daysUntil(iso: string | null): number {
  if (!iso) return DEFAULT_VALIDITY_DAYS;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / DAY));
}

function mapVerification(badge: unknown): EvidenceInfo["status"] {
  const s = str(badge).toLowerCase();
  if (s === "verified") return "verified";
  if (s === "flagged" || s === "quarantined") return "flagged";
  return "unverified";
}

// Canonical pursuit projection (R-3 · cross-tier read unification). The brief
// exposes signal.pursuit_ownership.canonical_stage_outcome — the
// v_signal_stage_outcome_current row, derived server-side from the full ordered
// ledger, including stage_regressed walk-backs and outcome_reopened (which
// clears the standing outcome). The stage now reads current_stage_key directly
// instead of the legacy pursuit_stage cache column.
//
// Parity with the legacy read this replaces: current_stage_key is 'won' exactly
// when the standing outcome is 'won' (the case the old code got from
// closed_outcome), and otherwise carries the same four effective-stage values
// the pursuit_stage cache held, with 'assigned' in place of null. Both are
// rebuilt from the same ledger predicate, ordering and tiebreak.
//
// The OUTCOME read below deliberately stays on the legacy closed_outcome field:
// lifecycle reads are out of scope for this cutover (R-3) and remain on legacy
// fields pending a projection/transport extension.
const STAGE_FROM_PROJECTION: Record<string, StageId> = {
  stage_outreach_made: "outreach_made",
  stage_meeting_scheduled: "meeting",
  stage_proposal: "proposal",
  won: "won",
  assigned: "assigned",
};

function projectedOutcome(pursuit: unknown): Outcome | null {
  const o = str(g(pursuit, "closed_outcome"));
  return o === "won" || o === "passed" ? (o as Outcome) : null;
}

export function projectedStage(pursuit: unknown): StageId {
  const canonical = g(pursuit, "canonical_stage_outcome");
  return STAGE_FROM_PROJECTION[str(g(canonical, "current_stage_key"))] ?? "assigned";
}

function actorName(actorType: HistoryActorType, recipientName: string): string {
  if (actorType === "token_recipient") return recipientName;
  if (actorType === "system") return "Signal Engine";
  return "Ankura team"; // human (assigner) — name not resolvable session-less
}

function mapHistory(entries: unknown[], recipientName: string): RecipientHistoryEntry[] {
  const atOf = (e: unknown) => str(g(e, "occurred_at")) || str(g(e, "created_at"));
  // The first magic-link open is the claim conversion (resolve marks first_open):
  // relabel just that earliest open as "claim" so the timeline reads as the
  // recipient claiming the signal; later opens stay plain opens.
  const firstOpenAt = entries
    .filter((e) => str(g(e, "event_type")) === "magic_link_open")
    .map(atOf)
    .sort()[0];
  return entries
    .map((e) => {
      const raw = str(g(e, "actor_type"));
      const actorType: HistoryActorType =
        raw === "system" ? "system" : raw === "token_recipient" ? "token_recipient" : "human";
      const ps = g(e, "payload_summary");
      const rawVerb = str(g(e, "event_type"));
      const at = atOf(e);
      const verb =
        rawVerb === "magic_link_open" && at === firstOpenAt ? "claim" : rawVerb;
      const outcome = strOrNull(g(ps, "outcome"));
      const reason = strOrNull(g(ps, "reason"));
      return {
        id: str(g(e, "signal_action_event_id")),
        actorType,
        actorName: actorName(actorType, recipientName),
        verb,
        detail:
          (outcome && reason ? `${outcome} · ${reason}` : outcome) ??
          strOrNull(g(ps, "stage")) ??
          strOrNull(g(ps, "to_stage")),
        at,
      };
    })
    // Server order varies; the Timeline reverses its input to show newest-first,
    // so hand it chronological (oldest-first) order.
    .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
}

function mapSignal(signal: unknown): RecipientSignal {
  const header = g(signal, "header");
  const content = g(signal, "content");
  const ev = g(signal, "evidence_verification");
  return {
    id: str(g(signal, "signal_id")),
    company: str(g(header, "company")) || "Unknown company",
    headline: str(g(header, "title")) || "Untitled signal",
    score: num(g(header, "scf_score")),
    tier: num(g(header, "tier")) || 3,
    verification: mapVerification(g(ev, "verification_badge")),
    updatedAt: str(g(header, "observed_at")),
    firstSeenAt: str(g(header, "observed_at")),
    summary: str(g(content, "summary")) || str(g(signal, "summary")),
    whyItMatters: str(g(content, "why_it_matters")),
    recommendedAction: str(g(content, "recommended_action")) || str(g(signal, "recommended_action")),
    talkTrack: str(g(content, "talk_track")) || str(g(signal, "talk_track")),
  };
}

function mapEvidence(signal: unknown): EvidenceInfo {
  const ev = g(signal, "evidence_verification");
  const header = g(signal, "header");
  return {
    sourceLabel: str(g(ev, "source")) || "Unknown source",
    sourceUrl: strOrNull(g(ev, "signal_url")),
    retrievedAt: str(g(header, "observed_at")),
    status: mapVerification(g(ev, "verification_badge")),
    deadlineAt: strOrNull(g(ev, "freshness_deadline_at")),
  };
}

function mapAccountPattern(ap: unknown): AccountPattern | null {
  if (!ap) return null;
  const prior = g(ap, "prior_ankura_engagement_summary");
  const count = num(g(prior, "engagement_count"));
  const practices = g(prior, "primary_practices");
  const related = Array.isArray(g(ap, "top_related_signals"))
    ? (g(ap, "top_related_signals") as unknown[]).map((s) => ({
        id: str(g(s, "signal_id")) || str(g(s, "id")),
        company: str(g(s, "company")) || str(g(ap, "organization_name")),
        headline: str(g(s, "headline")) || str(g(s, "feed_title")) || str(g(s, "title")),
        score: num(g(s, "scf_score")) || num(g(s, "score")),
      }))
    : [];
  const priorEngagement =
    count > 0
      ? `${count} prior Ankura engagement${count === 1 ? "" : "s"}` +
        (Array.isArray(practices) && practices.length ? ` · ${(practices as string[]).join(", ")}` : "")
      : null;
  return {
    company: str(g(ap, "organization_name")) || "This account",
    activeCount: num(g(ap, "active_signal_count_this_quarter")),
    related,
    priorEngagement,
  };
}

function mapActivation(ac: unknown): ActivationContext {
  const prior = g(ac, "prior_ankura_engagement_summary");
  const count = num(g(prior, "engagement_count"));
  const matches = num(g(ac, "uploaded_contact_match_count"));
  const practices = g(prior, "primary_practices");
  return {
    relationship: count > 0 ? "Existing Ankura relationship" : "No prior relationship",
    detail:
      matches > 0
        ? `${matches} matched contact${matches === 1 ? "" : "s"} on file`
        : "No matched contacts on file",
    monetization:
      Array.isArray(practices) && practices.length ? (practices as string[]).join(", ") : "Unranked",
  };
}

function mapContact(c: unknown): MatchedContact {
  const name =
    // find_contacts_proxy (Seamless) returns display_name; matched contacts use
    // name/full_name. Read display_name first so live search results are named.
    str(g(c, "display_name")) ||
    str(g(c, "name")) ||
    str(g(c, "full_name")) ||
    `${str(g(c, "first_name"))} ${str(g(c, "last_name"))}`.trim() ||
    "Contact";
  const strengthRaw = str(g(c, "strength")) || str(g(c, "match_strength"));
  const strength: MatchedContact["strength"] =
    strengthRaw === "strong" ? "strong" : strengthRaw === "weak" ? "weak" : "moderate";
  return {
    // linkedin is the stable per-person id from Seamless; fall back through the
    // usual keys so each row is distinct (no duplicate React keys).
    id:
      str(g(c, "contact_id")) ||
      str(g(c, "id")) ||
      str(g(c, "email")) ||
      str(g(c, "linkedin")) ||
      name,
    name,
    title: str(g(c, "title")) || str(g(c, "job_title")),
    strength,
    saved: g(c, "saved") === true,
  };
}

function whyForYou(raw: unknown): WhyForYou | null {
  if (!raw) return null;
  const contextNote = strOrNull(g(raw, "context_note"));
  const capabilityMatch = strOrNull(g(raw, "capability_match"));
  if (!contextNote && !capabilityMatch) return null;
  return { contextNote, capabilityMatch };
}

function recommendedAsset(raw: unknown): RecipientBrief["recommendedAsset"] {
  if (!raw || g(raw, "visible") !== true) return null;
  const match = g(raw, "match");
  if (!match) return null;
  return {
    title: str(g(match, "title")),
    kind: str(g(match, "kind")),
    reason: str(g(match, "reason")),
  };
}

function ownershipLabel(pursuit: unknown, recipientName: string): string {
  const state = str(g(pursuit, "ownership_state"));
  if (state === "claimed") return `Claimed by ${recipientName}`;
  if (state === "assigned") return `Assigned to ${recipientName}`;
  return "Unclaimed";
}

export class LiveRecipientTokenAdapter implements RecipientTokenAdapter {
  async getBrief(token: string): Promise<RecipientBrief | null> {
    // resolve first (marks first_open / claim conversion), then the full brief.
    const resolved = await tokenInvoke("resolve_magic_link", { token });
    if (!requireTokenRead("resolve_magic_link", resolved)) return null;
    const briefRes = await tokenInvoke("get_magic_link_signal_brief", { token });
    const briefData = requireTokenRead("get_magic_link_signal_brief", briefRes);
    if (!briefData) return null;

    const d = briefData;
    const viewer = g(d, "viewer");
    const recipientName = str(g(viewer, "display_name")) || "you";
    const signal = g(d, "signal");
    const history = Array.isArray(g(d, "action_history")) ? (g(d, "action_history") as unknown[]) : [];

    // Link metadata is exposed both at the top level of the brief and under
    // `magic_link`; read the top level first, fall back to the nested object.
    const link = g(d, "magic_link");
    const meta = (k: string): unknown => g(d, k) ?? g(link, k);
    const expiresAt = strOrNull(meta("expires_at"));
    const assignerName = str(meta("assigner_display_name")) || "the assigner";

    return {
      token,
      recipientName,
      assignerName,
      observedAt: str(g(g(signal, "header"), "observed_at")),
      // Assignment timestamp (parity amendment Item 6). The brief exposes it on
      // the recipient's assign event (why_for_you_context.assigned_at); fall back
      // to the signal-level pursuit_ownership.assigned_at. Null → the top bar
      // shows the observed date instead.
      assignedAt:
        strOrNull(g(g(d, "why_for_you_context"), "assigned_at")) ??
        strOrNull(g(g(signal, "pursuit_ownership"), "assigned_at")),
      // Real expiry + snooze budget from resolve/brief (Codex, addressing the W2
      // gaps); default only if the field is unexpectedly absent.
      expiresAt: expiresAt ?? new Date(Date.now() + DEFAULT_VALIDITY_DAYS * DAY).toISOString(),
      expiresInDays: daysUntil(expiresAt),
      snoozeUsed: num(meta("snooze_count")),
      snoozeMax: num(meta("snooze_max")) || DEFAULT_SNOOZE_MAX,
      reachedStage: projectedStage(g(signal, "pursuit_ownership")),
      outcome: projectedOutcome(g(signal, "pursuit_ownership")),
      signal: mapSignal(signal),
      whyForYou: whyForYou(g(d, "why_for_you_context")),
      accountPattern: mapAccountPattern(g(d, "account_pattern")),
      recommendedAsset: recommendedAsset(g(d, "recommended_asset")),
      matchedContacts: Array.isArray(g(d, "matched_contacts"))
        ? (g(d, "matched_contacts") as unknown[]).map(mapContact)
        : [],
      foundContacts: Array.isArray(g(d, "found_contacts"))
        ? (g(d, "found_contacts") as unknown[]).map(mapContact)
        : [],
      evidence: mapEvidence(signal),
      activation: mapActivation(g(d, "activation_context")),
      ownershipLabel: ownershipLabel(g(signal, "pursuit_ownership"), recipientName),
      followUpDueAt: strOrNull(g(signal, "follow_up_due_at")),
      history: mapHistory(history, recipientName),
    };
  }

  async resolveIdentity(token: string): Promise<{ assignerName: string } | null> {
    // resolve_magic_link exposes assigner_display_name for a live token (top
    // level / under magic_link) and, for a revoked or expired token, under
    // `data.fallback` (an invalid/garbage token resolves no identity). Read all
    // three so the expired fallback names the assigner whenever the link is
    // known; otherwise null → generic "the person who shared this link" copy.
    const r = await tokenInvoke("resolve_magic_link", { token });
    const d = r.data;
    const name = str(
      g(d, "assigner_display_name") ||
        g(g(d, "magic_link"), "assigner_display_name") ||
        g(g(d, "fallback"), "assigner_display_name"),
    );
    return name ? { assignerName: name } : null;
  }

  async recordStageEvent(token: string, stage: StageId): Promise<void> {
    // Only the three forward stages are recordable via record_stage_event; "won"
    // is an outcome (recordOutcome) and "assigned" is the initial state.
    const eventType =
      stage === "outreach_made"
        ? "stage_outreach_made"
        : stage === "meeting"
          ? "stage_meeting_scheduled"
          : stage === "proposal"
            ? "stage_proposal"
            : null;
    if (!eventType) return;
    const r = await tokenInvoke("record_stage_event", { token, event_type: eventType });
    if (!r.ok) throw new Error(`record_stage_event: ${r.status}`);
  }

  async recordOutcome(token: string, outcome: Outcome, passReason?: PassReason): Promise<void> {
    const reason = passReason
      ? passReason.note?.trim() || passReasonLabel(passReason.code)
      : undefined;
    const r = await tokenInvoke("record_outcome", {
      token,
      outcome,
      ...(reason ? { reason } : {}),
      ...(passReason ? { payload: { reason_code: passReason.code } } : {}),
    });
    if (!r.ok) throw new Error(`record_outcome: ${r.status}`);
  }

  async reopenPursuit(token: string): Promise<void> {
    // Distinct status handling (deliberately NOT routed through recipientAction,
    // which collapses every failure to one generic Error): 403 = the link's
    // allowed_actions omit outcome_reopened (a link minted before this feature);
    // 409 = the pursuit is no longer closed. 201 needs no body-trust — the brief
    // refetch reads the projection, which the server flips to active on the
    // outcome_reopened ledger event.
    const r = await tokenInvoke("record_magic_link_recipient_action", {
      token,
      action: "outcome_reopened",
    });
    if (r.status === 403) throw new ReopenNotAllowedError();
    if (r.status === 409) throw new ReopenConflictError();
    if (!r.ok) {
      throw new Error(
        `record_magic_link_recipient_action(outcome_reopened): ${r.status}`,
      );
    }
  }

  async regressStage(
    _token: string,
    _toStage: StageId,
    _reason?: string,
  ): Promise<void> {
    // R-P4-5 / R-1: the token tier has no regression. The bearer path answers
    // every stage_regressed with a 403 and writes nothing, so this issues no
    // record_stage_event invocation at all — the refusal is typed and local.
    // The recipient surface exposes no walk-back affordance; this member exists
    // only to keep the refusal explicit on the adapter contract.
    throw new RegressionNotAllowedError();
  }

  async snooze(token: string): Promise<SnoozeResult> {
    const r = await tokenInvoke("snooze_magic_link", { token });
    if (!r.ok || !r.data) {
      if (r.status === 429 || /snooze/i.test(JSON.stringify(r.data ?? {}))) {
        throw new SnoozeCapError();
      }
      throw new Error(`snooze_magic_link: ${r.status}`);
    }
    const ml = g(r.data, "magic_link");
    const expiresAt = str(g(ml, "expires_at"));
    return {
      expiresAt,
      expiresInDays: daysUntil(expiresAt),
      snoozeUsed: num(g(ml, "snooze_count")),
    };
  }

  async runRelationshipCheck(token: string): Promise<SignalRelationshipResult> {
    // record_magic_link_recipient_action now returns { event, result } for
    // relationship_check_run; extract the grouped-by-owner result for the panel.
    const data = await this.recipientAction(token, "relationship_check_run");
    const result = data?.["result"] as SignalRelationshipResult | undefined;
    if (!result) {
      throw new Error(
        "record_magic_link_recipient_action(relationship_check_run): missing relationship result payload",
      );
    }
    return result;
  }

  async runTariffScan(token: string): Promise<void> {
    await this.recipientAction(token, "tariff_scan_run");
  }

  async sendAsset(token: string, assetTitle: string): Promise<void> {
    await this.recipientAction(token, "asset_sent", { asset_title: assetTitle });
  }

  async setFollowUp(token: string, dueAt: string): Promise<void> {
    await this.recipientAction(token, "follow_up_set", { due_at: dueAt });
  }

  private async recipientAction(
    token: string,
    action: string,
    extra: Record<string, unknown> = {},
  ): Promise<Record<string, unknown> | undefined> {
    const r = await tokenInvoke("record_magic_link_recipient_action", {
      token,
      action,
      ...extra,
    });
    if (!r.ok) throw new Error(`record_magic_link_recipient_action(${action}): ${r.status}`);
    return r.data;
  }

  async findContacts(token: string): Promise<MatchedContact[]> {
    const r = await tokenInvoke("find_contacts_proxy", { token });
    if (isDead(r)) throw new Error("find_contacts_proxy: link is no longer active");
    const cap = g(r.data, "cap");
    const seamlessErr = str(g(g(r.data, "seamless"), "error_category"));
    if (
      r.status === 429 ||
      seamlessErr === "cap_reached" ||
      (cap && num(g(cap, "used_today")) > num(g(cap, "daily_cap")))
    ) {
      throw new CapReachedError();
    }
    const contacts = g(r.data, "contacts");
    return Array.isArray(contacts) ? contacts.map(mapContact) : [];
  }
}
